import { ToolbarMode } from '@fuse/types/fuse-config';
import { Injectable, ViewContainerRef, signal, computed } from '@angular/core';
import { ScrollBarService } from '@shared/scroll/scrollbar.service';
import { Observable, filter, ReplaySubject, merge, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, mergeMap, tap } from 'rxjs/operators';
import { Router, Scroll, ActivationEnd, RoutesRecognized, NavigationEnd, NavigationStart } from '@angular/router';
import { FuseMatchMediaService } from '@fuse/services/match-media.service';
import { Platform } from '@angular/cdk/platform';
import { TransferState, makeStateKey } from '@angular/core';

export const TOLLBAR_MODE = makeStateKey<any>('expo.toolbar-mode');
export interface ToolbarState {
  toolbarMode: ToolbarMode;
  collapsed: boolean;
  transparent: boolean;
  hidden?: boolean;
}
export interface ToolbarAnimationEvent {
  collapse: boolean;
  state: 'start' | 'end';
  matchingMedia?: string[];
}
@Injectable({
  providedIn: 'root'
})
export class ToolbarService {
  toolbarMode = signal<ToolbarMode>(ToolbarMode.solid);
  isScrolled = signal(false);
  matchingAliases = this.fuseMediaMatchService.matchingAliases;
  toolbarExpandCollapseStartSubject = new Subject<boolean>;
  scrollSubscription: Subscription;

  #leftSidebarToggleOpen = signal(false);
  leftSidebarToggleOpen = this.#leftSidebarToggleOpen.asReadonly();
  #rightSidebarToggleOpen = signal(false);
  rightSidebarToggleOpen = this.#rightSidebarToggleOpen.asReadonly();

  skipAnimation = false;
  hideToolbar = signal(false);

  private toolbarStateChangeSubject = new ReplaySubject<ToolbarState>();
  private toolbarAnimationSubject = new Subject<ToolbarAnimationEvent>();
  constructor(
    private scrollBarService: ScrollBarService,
    private fuseMediaMatchService: FuseMatchMediaService,
    public platform: Platform,
    private router: Router,
    private readonly transferState: TransferState
  ) {
    if (this.platform.isBrowser) {
      if (this.transferState.hasKey<ToolbarMode>(TOLLBAR_MODE)) {
        this.toolbarMode.set(this.transferState.get<ToolbarMode>(TOLLBAR_MODE, ToolbarMode.solid));
      }
    }

    this.router.events
    .subscribe((e) => {
      if (e instanceof RoutesRecognized) {
        if (this.scrollSubscription) {
          this.scrollSubscription.unsubscribe();
        }
        const data = e.state.root.firstChild?.data;
        this.hideToolbar.set(data.hideToolbar);
        this.toolbarMode.set(data.toolbarMode?? ToolbarMode.solid);
        this.toolbarStateChangeSubject.next({
          toolbarMode: this.toolbarMode(),
          collapsed: this.isCollapsed(),
          transparent: this.isTransparent(),
          hidden: this.hideToolbar()
        });
      }

      if (e instanceof ActivationEnd && e.snapshot.outlet === 'primary') {

        if (e.snapshot.children.length > 0) {
          const child = e.snapshot.children[0];
          if (child.data.toolbarMode) {
            // già processato
            return;
          }
        }
        const data = e.snapshot.data;
        this.hideToolbar.set(data.hideToolbar);
        this.toolbarMode.set(data.toolbarMode ?? ToolbarMode.solid);
        if (! this.platform.isBrowser) {
          this.transferState.set(TOLLBAR_MODE, <ToolbarMode> this.toolbarMode());
        }
      }
      if (e instanceof NavigationEnd) {
        if (this.platform.isBrowser) {
          setTimeout(() => {
            this.toolbarExpandCollapseStartSubject.next(this.isCollapsed());
            setTimeout(() => {
              this.scrollSubscription = this.$Scroll
              .pipe(
                map(() => {
                  return this.isCollapsed();
                })
              )
              .subscribe((state) => {             
                this.toolbarExpandCollapseStartSubject.next(state);
              });
            }, 150); // wait scroll restore;
            
          }, 20); // wait router Scroll Event
        }
      }
      if (e instanceof Scroll) {
        const position  = e.position ? e.position[1]: 0;
        this.isScrolled.set(position > 0);
        this.skipAnimation = (position === 0);
        setTimeout(()=> this.skipAnimation = false, 500);
        this.toolbarStateChangeSubject.next({
          toolbarMode: this.toolbarMode(),
          collapsed: this.isCollapsed(),
          transparent: this.isTransparent(),
          hidden: this.hideToolbar()
        });
      }
    });


  }

  isCollapsed = computed(() => {
    if (!this.platform.isBrowser) {
      return false;
    }
    if (this.matchingAliases().includes('lg')) {
      // return this.scrollTop > 0;
      return this.isScrolled();

    } else {
      return false;
    }
  });

  isSolid = computed(() => {
    if (this.hideToolbar()) {
      return false;
    }
    if(this.platform.isBrowser) {
      if ((!this.matchingAliases().includes('lg')) || this.toolbarMode() === ToolbarMode.solid) {
        return true;
      } else {
        return this.toolbarMode() === ToolbarMode.solid;
      }
    } else {
      return this.toolbarMode() === ToolbarMode.solid;
    }

  });

  isTransparent = computed(() => {
    if (!this.platform.isBrowser) {
      return this.toolbarMode() !== ToolbarMode.solid;
    }

    if (this.toolbarMode() !== ToolbarMode.solid && this.fuseMediaMatchService.matchingAliases().includes('lg')) {
      return this.toolbarMode() === ToolbarMode.both ? !this.isCollapsed() : true;
    } else {
      return this.toolbarMode() === ToolbarMode.both;
    }
  });

  get $Scroll(): Observable<number> {
    if (!this.platform.isBrowser) {
      return of(0);
    }
    if (this._$Scroll){
      return this._$Scroll;
    }
    const scrollBar = this.scrollBarService.mainScrollbar;
    return this._$Scroll = this.scrollBarService.monitorScrollTop(scrollBar, false)
    .pipe(
      filter(() => this.toolbarMode() !== ToolbarMode.solid && this.skipAnimation === false),
      map(([prev, curr]) => curr),
      distinctUntilChanged(),
      tap((curr) => {
        this.isScrolled.set(curr > 0);
      }
      )
    );
  }
  private _$Scroll: Observable<number> = null;
  private _toolbarStateChange$: Observable<ToolbarState>
  get toolbarStateChange$(): Observable<ToolbarState> {
    if (!this._toolbarStateChange$) {
      this._toolbarStateChange$ =  this.platform.isBrowser ?
      merge(
        this.toolbarStateChangeSubject.asObservable()
      .pipe(
        mergeMap(() => {
          return  this.fuseMediaMatchService.onMediaChange$.pipe(
            map((media) => media.matchingAliases),
            map(() => {
              return {
                toolbarMode: this.toolbarMode(),
                collapsed: this.isCollapsed(),
                transparent: this.isTransparent(),
                hidden: this.hideToolbar()
              };
            })
          );
        }),
        distinctUntilChanged((prev, cur) =>  JSON.stringify(prev) === JSON.stringify(cur))
      )) : this.toolbarStateChangeSubject.asObservable(); // da server;
    }
    return this._toolbarStateChange$;
  }


  get toolbarExpandCollapseStart$(): Observable<boolean> {
    return  this.toolbarExpandCollapseStartSubject.asObservable();
  }

  get onAnimationStateChange(): Observable<ToolbarAnimationEvent> {
    return this.toolbarAnimationSubject.asObservable();
  }

  toggleLeftSidebarOpen(): void {
    this.#leftSidebarToggleOpen.update(value => !value);
  }

  toggleRightSidebarOpen(): void {
    this.#rightSidebarToggleOpen.update(value => !value);
  }

  setAnimationState(collapse: boolean, state: 'start' | 'end'): void {
    this.toolbarAnimationSubject.next({collapse: collapse, state: state, matchingMedia: this.matchingAliases()});
  }

  async showNewsletterSubscribeDialog(vc?: ViewContainerRef) {
    const { NewsletterSigninDialogComponent } = await import('app/shared/components/newsletter-signin/newsletter-signin-dialog/newsletter-signin-dialog.component');
    const dialog = vc.createComponent(NewsletterSigninDialogComponent);
    dialog.instance.openDialog();
  }
}
